import React, { useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useSelectedLocation } from './LocationContext';

const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Year-End Report — professional income-statement (P&L style) annual report.
 * 12 location-local months (each location's own timezone) + an FY TOTAL
 * column. Sections: Revenue → Deductions → Net revenue, then memo lines
 * (taxes, tips, order counts) and channel mix. Print-ready single page.
 */
const YearEndTab = ({ selectedLocationIds = [] }) => {
  const locationCtx = useSelectedLocation();
  const workingLocation = locationCtx?.selectedLocation || null;
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await httpsCallable(getFunctions(), 'getYearEndReport')({ year });
      setData(res.data);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year]);

  // The months shown, filtered to the selected locations when the page
  // selector has entries (the report's per-month location rows merge them).
  const months = useMemo(() => {
    if (!data) return [];
    const hasSel = selectedLocationIds.length > 0;
    return data.months.map((m) => {
      const locRows = hasSel ? m.perLocation.filter((l) => selectedLocationIds.includes(l.locationId)) : m.perLocation;
      // Recompute the month totals from the visible location rows
      const totals = locRows.reduce((acc, l) => {
        for (const k of ['gross', 'discounts', 'refunds', 'net', 'orders', 'items', 'canceled', 'taxes', 'tips', 'processingFeesCollected', 'stripeFees', 'platformFees', 'cash', 'card', 'open', 'other']) {
          acc[k] = (acc[k] || 0) + (l[k] || 0);
        }
        for (const [ck, cv] of Object.entries(l.channels || {})) {
          acc.channels = acc.channels || {};
          acc.channels[ck] = (acc.channels[ck] || 0) + cv;
        }
        return acc;
      }, {});
      return { ...m, totals, perLocation: locRows };
    });
  }, [data, selectedLocationIds]);

  const yearTotals = useMemo(() => {
    return months.reduce((acc, m) => {
      for (const [k, v] of Object.entries(m.totals)) {
        if (k === 'channels') {
          acc.channels = acc.channels || {};
          for (const [ck, cv] of Object.entries(v || {})) acc.channels[ck] = (acc.channels[ck] || 0) + cv;
        } else {
          acc[k] = (acc[k] || 0) + (v || 0);
        }
      }
      return acc;
    }, {});
  }, [months]);
  if (yearTotals.orders > 0) yearTotals.avgTicket = yearTotals.net / yearTotals.orders;

  if (!workingLocation && selectedLocationIds.length === 0) {
    return (
      <div className="card mb-4"><div className="card-body text-muted" style={{ fontSize: 14 }}>
        Select a working location (top bar) to see the year-end report.
      </div></div>
    );
  }

  const generatedAt = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  const yearOptions = [];
  for (let y = new Date().getFullYear(); y >= 2025; y--) yearOptions.push(y);

  // Line items: [label, key, kind] — kind: rev | less | net | memo | section
  const LINES = [
    ['section', 'REVENUE'],
    ['Gross sales', 'gross', 'rev'],
    ['Discounts', 'discounts', 'less'],
    ['Refunds', 'refunds', 'less'],
    ['section', 'PROCESSING & PLATFORM FEES'],
    ['Processing fees (collected)', 'processingFeesCollected', 'less'],
    ['Stripe fees (cost)', 'stripeFees', 'less'],
    ['Platform fee (online)', 'platformFees', 'less'],
    ['net', 'net', 'net'],
    ['section', 'MEMO'],
    ['Taxes collected', 'taxes', 'memo'],
    ['Tips', 'tips', 'memo'],
    ['Orders', 'orders', 'count'],
    ['Items sold', 'items', 'count'],
    ['Average ticket (net)', 'avgTicket', 'rev'],
    ['Canceled orders (excluded)', 'canceled', 'count'],
  ];

  const cellVal = (totals, key, kind) => {
    const v = totals?.[key];
    if (kind === 'count') return (v ?? 0).toLocaleString();
    if (kind === 'less') return v ? `− ${money(v)}` : '—';
    return money(v);
  };

  return (
    <div className="card mb-4 year-end-report" style={{ border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
      <style>{`@media print { body * { visibility: hidden; } .year-end-report, .year-end-report * { visibility: visible; } .year-end-report { position: absolute; left: 0; top: 0; width: 100%; border: none; } .year-end-no-print { display: none !important; } }`}</style>
      <div style={{ background: '#1f2937', color: '#fff', padding: '18px 24px' }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#f97316', fontWeight: 800 }}>Year-End Report</div>
            <h4 className="mb-0 font-weight-bold" style={{ color: '#fff' }}>FY {year} — Income Statement</h4>
            <div style={{ fontSize: 13, color: '#9ca3af' }}>Months follow each location's timezone</div>
          </div>
          <div className="d-flex align-items-center year-end-no-print" style={{ gap: 8 }}>
            <select className="form-control form-control-sm" style={{ width: 110 }} value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))}>
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button className="btn btn-sm btn-outline-light" onClick={load} disabled={loading}><i className="fas fa-rotate"></i></button>
            <button className="btn btn-sm btn-warning" style={{ background: '#f97316', borderColor: '#f97316', color: '#fff' }} onClick={() => window.print()}>
              <i className="fas fa-print mr-1"></i>Print
            </button>
          </div>
        </div>
      </div>

      <div className="card-body" style={{ padding: '16px 24px 20px' }}>
        {error && <div className="alert alert-danger">{error}</div>}
        {loading || !data ? (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm" style={{ color: '#f97316' }} /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-sm" style={{ fontSize: 12.5, marginBottom: 0, minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={{ borderTop: 'none', minWidth: 190 }}></th>
                  {months.map((m) => (
                    <th key={m.month} style={{ borderTop: 'none', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {MONTH_SHORT[parseInt(m.month.slice(5, 7), 10) - 1]}
                    </th>
                  ))}
                  <th style={{ borderTop: 'none', textAlign: 'right', color: '#f97316', fontWeight: 800, whiteSpace: 'nowrap' }}>FY {year}</th>
                </tr>
              </thead>
              <tbody>
                {LINES.map((row) => {
                  if (row[0] === 'section') {
                    return (
                      <tr key={row[1]}>
                        <td colSpan={months.length + 1} style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, color: '#9ca3af', paddingTop: 14, borderBottom: 'none' }}>{row[1]}</td>
                      </tr>
                    );
                  }
                  const [label, key, kind] = row;
                  const isNet = kind === 'net';
                  return (
                    <tr key={key} style={isNet ? { borderTop: '2px solid #1f2937', borderBottom: '2px solid #1f2937' } : {}}>
                      <td style={{ color: isNet ? '#111827' : kind === 'less' ? '#6b7280' : '#1f2937', fontWeight: isNet ? 800 : 500 }}>{isNet ? 'NET REVENUE' : label}</td>
                      {months.map((m) => (
                        <td key={m.month} style={{ textAlign: 'right', whiteSpace: 'nowrap', color: kind === 'less' ? '#6b7280' : '#111827', fontWeight: isNet ? 800 : 600 }}>
                          {cellVal(m.totals, key, kind)}
                        </td>
                      ))}
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap', color: '#f97316', fontWeight: 800 }}>
                        {cellVal(yearTotals, key, kind)}
                      </td>
                    </tr>
                  );
                })}
                {/* Channel mix */}
                <tr>
                  <td colSpan={months.length + 1} style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, color: '#9ca3af', paddingTop: 14, borderBottom: 'none' }}>CHANNEL MIX (GROSS)</td>
                </tr>
                {['POS', 'Kiosk', 'Online', 'Other'].map((ch) => (
                  <tr key={ch}>
                    <td style={{ color: '#6b7280', fontWeight: 500 }}>{ch}</td>
                    {months.map((m) => (
                      <td key={m.month} style={{ textAlign: 'right', whiteSpace: 'nowrap', color: '#111827' }}>
                        {m.totals?.channels?.[ch] ? money(m.totals.channels[ch]) : '—'}
                      </td>
                    ))}
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap', color: '#f97316', fontWeight: 800 }}>
                      {yearTotals.channels?.[ch] ? money(yearTotals.channels[ch]) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-center mt-3" style={{ fontSize: 11, color: '#9ca3af' }}>
              Generated {generatedAt} · FodyFee · FY {year} · for accounting review, not a filed document
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default YearEndTab;
